import type { CategoryItem } from "@/types";

export type ParentGroupedRows<TRow> = {
  parent: TRow;
  parentMeta?: CategoryItem;
  children: TRow[];
  syntheticParent: boolean;
};

/**
 * Groups rows by parent category with optional synthetic parents when only child rows exist.
 * Shared by cashflow + forecast views to keep parent/subcategory behavior consistent.
 */
export function buildParentCategoryGroups<TRow>(
  rows: TRow[],
  categoriesById: Map<string, CategoryItem>,
  type: "income" | "expense",
  getRowCategoryId: (row: TRow) => string,
  makeSyntheticParent: (parentMeta: CategoryItem) => TRow,
): ParentGroupedRows<TRow>[] {
  const parentRows = new Map<string, TRow>();
  const childrenByParent = new Map<string, TRow[]>();
  const syntheticParents = new Map<string, TRow>();

  for (const row of rows) {
    const rowCategoryId = getRowCategoryId(row);
    const rowMeta = categoriesById.get(rowCategoryId);
    const parentId = rowMeta?.parentCategoryId;
    if (parentId) {
      const childRows = childrenByParent.get(parentId) ?? [];
      childRows.push(row);
      childrenByParent.set(parentId, childRows);
      if (!parentRows.has(parentId)) {
        const parentMeta = categoriesById.get(parentId);
        if (parentMeta && parentMeta.type === type) {
          syntheticParents.set(parentId, makeSyntheticParent(parentMeta));
        }
      }
      continue;
    }
    parentRows.set(rowCategoryId, row);
  }

  for (const [parentId, synthetic] of syntheticParents.entries()) {
    if (!parentRows.has(parentId)) {
      parentRows.set(parentId, synthetic);
    }
  }

  const grouped: ParentGroupedRows<TRow>[] = [];
  for (const row of rows) {
    const rowCategoryId = getRowCategoryId(row);
    if (!parentRows.has(rowCategoryId)) continue;
    grouped.push({
      parent: row,
      parentMeta: categoriesById.get(rowCategoryId),
      children: childrenByParent.get(rowCategoryId) ?? [],
      syntheticParent: false,
    });
  }

  for (const [parentId, parent] of parentRows.entries()) {
    const exists = grouped.some(
      (group) => getRowCategoryId(group.parent) === parentId,
    );
    if (exists) continue;
    grouped.push({
      parent,
      parentMeta: categoriesById.get(parentId),
      children: childrenByParent.get(parentId) ?? [],
      syntheticParent: true,
    });
  }

  return grouped;
}
